import { Component } from "@theme/component";
import { StandardEvents } from "@shopify/events";
import { DrawerOpenEvent } from "@theme/theme-drawer";
//#region assets/cart-drawer.ts
/**
* A custom element that manages cart drawer behavior within a `<theme-drawer>`.
*
* Dialog lifecycle (open/close, squeeze, history, animations) is owned by `<theme-drawer>`.
* The `cart:view` event is auto-dispatched by `CartItemsComponent` via the
* `view-event-trigger="dialog"` attribute (see `snippets/cart-items-component.liquid`).
* Cart count announcements are owned by `<header-actions>`.
* This component handles the remaining cart-specific concerns: auto-open on add-to-cart,
* sticky summary layout, and the installments CTA close-on-click.
*
* @extends {Component}
*/
var CartDrawerComponent = class extends Component {
	/** @type {number} */
	#summaryThreshold = .5;
	/** @type {import('@theme/theme-drawer').ThemeDrawer | null} */
	get #themeDrawer() {
		return this.closest("theme-drawer");
	}
	/** @type {HTMLDialogElement | null} */
	get #dialog() {
		return this.closest("dialog");
	}
	connectedCallback() {
		super.connectedCallback();
		document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
		this.#themeDrawer?.addEventListener(DrawerOpenEvent.eventName, this.#handleDrawerOpen);
		if (this.#themeDrawer?.hasAttribute("open")) this.#handleDrawerOpen();
	}
	disconnectedCallback() {
		super.disconnectedCallback();
		document.removeEventListener(StandardEvents.cartLinesUpdate, this.#handleCartLinesUpdate);
		this.#themeDrawer?.removeEventListener(DrawerOpenEvent.eventName, this.#handleDrawerOpen);
	}
	/**
	* Handles the theme-drawer opening — updates sticky state and wires up the installments CTA.
	*/
	#handleDrawerOpen = () => {
		this.#updateStickyState();
		customElements.whenDefined("shopify-payment-terms").then(() => {
			(this.querySelector("shopify-payment-terms")?.shadowRoot?.querySelector("#shopify-installments-cta"))?.addEventListener("click", () => this.#themeDrawer?.close(), { once: true });
		});
	};
	/**
	* @param {import('@shopify/events').CartLinesUpdateEvent} event
	*/
	#handleCartLinesUpdate = (event) => {
		if (document.querySelector("[data-vp-cart-panel]")) {
			const drawer = this.#themeDrawer;
			if (drawer) {
				drawer.removeAttribute("open");
				const dialog = drawer.querySelector("dialog");
				if (dialog instanceof HTMLDialogElement && dialog.open) dialog.close();
			}
			document.querySelector(".page-wrapper")?.classList.remove("page-wrapper--drawer-open");
			return;
		}
		const shouldAutoOpen = this.hasAttribute("auto-open") && event.action === "add" && !this.#themeDrawer?.isOpen;
		const sourceModal = event.target instanceof Element ? event.target.closest("dialog:modal") : null;
		if (shouldAutoOpen && !sourceModal && !this.#isCartEmpty()) this.#themeDrawer?.open();
		event.promise?.then(({ detail }) => {
			const settle = () => requestAnimationFrame(() => this.#updateStickyState());
			if (!shouldAutoOpen || detail?.didError) {
				settle();
				return;
			}
			const openAndSettle = () => {
				if (!this.#themeDrawer?.isOpen) this.#themeDrawer?.open();
				settle();
			};
			if (sourceModal?.open) sourceModal.addEventListener("close", openAndSettle, { once: true });
			else openAndSettle();
		}).catch((error) => {
			if (error?.name !== "AbortError") console.warn("[cart-drawer] Event promise rejected:", error);
		});
	};
	#isCartEmpty() {
		return Boolean(this.querySelector(".cart-drawer--empty"));
	}
	#updateStickyState() {
		const dialog = this.#dialog;
		if (!dialog) return;
		const content = dialog.querySelector(".cart-drawer__content");
		const summary = dialog.querySelector(".cart-drawer__summary");
		if (!content || !summary) {
			dialog.setAttribute("cart-summary-sticky", "false");
			return;
		}
		const drawerHeight = dialog.getBoundingClientRect().height;
		const ratio = summary.getBoundingClientRect().height / drawerHeight;
		dialog.setAttribute("cart-summary-sticky", ratio > this.#summaryThreshold ? "false" : "true");
	}
};
if (!customElements.get("cart-drawer-component")) customElements.define("cart-drawer-component", CartDrawerComponent);
//#endregion
